import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack,
  Typography, MenuItem, Autocomplete, Chip, Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/auth.store';
import { useTeamStore } from '../../store/team.store';
import { useEmployeeStore } from '../../store/employee.store';
import {
  CreateObjectiveRequest,
  Objective,
  ObjectiveOwner,
  UpdateObjectiveRequest,
} from '../../types/objective.type';
import { rfc3339ToDateInput } from '../../utils/taskApi.utils';

interface OwnerOption {
  type: 'employee' | 'manager';
  id: string;
  name: string;
  label: string;
}

interface Props {
  open: boolean;
  objective?: Objective | null;
  onClose: () => void;
  onSubmit: (data: CreateObjectiveRequest | UpdateObjectiveRequest) => Promise<void>;
}

export default function ObjectiveFormDialog({ open, objective, onClose, onSubmit }: Props) {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { teams, fetchTeams } = useTeamStore();
  const { employees, fetchEmployees } = useEmployeeStore();
  const isEdit = !!objective;

  const [title, setTitle] = useState('');
  const [teamId, setTeamId] = useState('');
  const [metric, setMetric] = useState('');
  const [targetValue, setTargetValue] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [selectedOwners, setSelectedOwners] = useState<OwnerOption[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      fetchTeams();
      fetchEmployees();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (objective) {
      setTitle(objective.title);
      setTeamId(objective.team_id ?? '');
      setMetric(objective.metric ?? '');
      setTargetValue(objective.target_value ? String(objective.target_value) : '');
      setDueDate(rfc3339ToDateInput(objective.due_date));
      setSelectedOwners(
        (objective.owners ?? []).map(o => ({
          ...o,
          label: o.name,
        })),
      );
    } else {
      setTitle('');
      setTeamId('');
      setMetric('');
      setTargetValue('');
      setDueDate('');
      setSelectedOwners(
        user
          ? [{ type: 'manager', id: user.id, name: user.name, label: user.name }]
          : [],
      );
    }
  }, [open, objective, user]);

  const ownerOptions = useMemo((): OwnerOption[] => {
    const options: OwnerOption[] = [];
    if (user) {
      options.push({
        type: 'manager',
        id: user.id,
        name: user.name,
        label: `${user.name} (${t('objectives.ownerTypes.manager')})`,
      });
    }
    for (const emp of employees) {
      options.push({
        type: 'employee',
        id: emp.id,
        name: emp.name,
        label: `${emp.name} (${t('objectives.ownerTypes.employee')})`,
      });
    }
    return options;
  }, [employees, user, t]);

  const handleSubmit = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      const owners: ObjectiveOwner[] = selectedOwners.map(o => ({
        type: o.type,
        id: o.id,
        name: o.name,
      }));
      const payload: CreateObjectiveRequest | UpdateObjectiveRequest = {
        title: title.trim(),
        team_id: teamId || undefined,
        metric: metric.trim() || undefined,
        target_value: targetValue ? Number(targetValue) : undefined,
        due_date: dueDate || undefined,
        owners: owners.length > 0 ? owners : undefined,
      };
      if (isEdit && !metric.trim()) payload.metric = null;
      if (isEdit && !dueDate) payload.due_date = null;
      await onSubmit(payload);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {isEdit ? t('objectives.editObjective') : t('objectives.createObjective')}
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <Box>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 0.75, color: '#374151' }}>
              {t('objectives.fields.title')} <span style={{ color: '#EF4444' }}>*</span>
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder={t('objectives.fields.titlePlaceholder')}
            />
          </Box>

          <Box>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 0.75, color: '#374151' }}>
              {t('objectives.fields.team')} <span style={{ color: '#EF4444' }}>*</span>
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={teamId}
              onChange={e => setTeamId(e.target.value)}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected) => {
                  if (!selected) {
                    return (
                      <Typography component="span" sx={{ color: '#9CA3AF', fontSize: '0.875rem' }}>
                        {t('objectives.fields.teamPlaceholder')}
                      </Typography>
                    );
                  }
                  return teams.find(team => team.id === selected)?.name ?? '';
                },
              }}
            >
              {teams.map(team => (
                <MenuItem key={team.id} value={team.id}>{team.name}</MenuItem>
              ))}
            </TextField>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5 }}>
              {t('objectives.fields.teamHint')}
            </Typography>
          </Box>

          <Box>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 0.75, color: '#374151' }}>
              {t('objectives.fields.metric')}
            </Typography>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              value={metric}
              onChange={e => setMetric(e.target.value)}
              placeholder={t('objectives.fields.metricPlaceholder')}
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <Box>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 0.75, color: '#374151' }}>
                {t('objectives.fields.targetValue')}
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="number"
                inputProps={{ min: 0, step: 'any' }}
                value={targetValue}
                onChange={e => setTargetValue(e.target.value)}
              />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 0.75, color: '#374151' }}>
                {t('objectives.fields.dueDate')}
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={dueDate}
                onChange={e => setDueDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>

          <Box>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 0.75, color: '#374151' }}>
              {t('objectives.fields.owners')}
            </Typography>
            <Autocomplete
              multiple
              options={ownerOptions}
              value={selectedOwners}
              onChange={(_, value) => setSelectedOwners(value)}
              getOptionLabel={o => o.label}
              isOptionEqualToValue={(a, b) => a.id === b.id && a.type === b.type}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={`${option.type}-${option.id}`}
                    label={option.name}
                    size="small"
                  />
                ))
              }
              renderInput={params => (
                <TextField {...params} size="small" placeholder={t('objectives.fields.ownersPlaceholder')} />
              )}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!title.trim() || !teamId || saving}
          sx={{ textTransform: 'none' }}
        >
          {saving ? t('common.loading') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
